/**
 * AI Garage Frontend Configuration
 * Supports both build-time and runtime environment variables
 * Runtime window._env_ (from env-config.js generated at container start) overrides build-time process.env
 */

/**
 * Get environment variable with runtime override support
 * @param {string} key - Environment variable key (e.g., 'REACT_APP_API_URL')
 * @returns {any} Value from runtime (window._env_) or build-time (process.env)
 */
function reactAppEnv(key) {
  const runtime = window._env_?.[key];
  if (runtime !== undefined && runtime !== null && String(runtime).trim() !== '') {
    return runtime;
  }
  return process.env[key];
}

/** Reject stale template / placeholder values baked into old builds or .env.production. */
function isUsableApiUrl(url) {
  if (!url || typeof url !== 'string') return false;
  const trimmed = url.trim();
  if (trimmed === '' || trimmed === 'undefined' || trimmed === 'null') return false;
  try {
    const parsed = new URL(trimmed);
    const host = parsed.hostname.toLowerCase();
    if (host === 'api.example.com') return false;
    if (host.includes('xxxxxx') || host.includes('xxxx')) return false;
    return parsed.protocol === 'https:'
      || host === 'localhost'
      || host === '127.0.0.1';
  } catch {
    return false;
  }
}

/** True for local browser hosts (loopback or private LAN). */
function isLocalBrowserHost(hostname) {
  if (!hostname) return false;
  const host = hostname.toLowerCase();
  if (host === 'localhost' || host === '127.0.0.1') return true;
  // CRA "On Your Network" URLs (e.g. 10.x / 192.168.x) should still use local API + DEV_MODE
  if (/^10\.\d+\.\d+\.\d+$/.test(host)) return true;
  if (/^192\.168\.\d+\.\d+$/.test(host)) return true;
  if (/^172\.(1[6-9]|2\d|3[0-1])\.\d+\.\d+$/.test(host)) return true;
  return false;
}

/**
 * Get API URL with runtime override support.
 * Single-container deploy: always use the browser's current origin so both
 * Cloud Run URLs (e.g. *.run.app and *-uc.a.run.app) work without CORS issues.
 */
const getApiUrl = () => {
  const runtime = window._env_?.REACT_APP_API_URL;
  const baked = process.env.REACT_APP_API_URL;

  // Local split-stack: prefer explicit API URL, else backend on :8000
  if (typeof window !== 'undefined' && isLocalBrowserHost(window.location.hostname)) {
    if (isUsableApiUrl(runtime)) {
      return String(runtime).replace(/\/$/, '');
    }
    if (isUsableApiUrl(baked)) {
      return String(baked).replace(/\/$/, '');
    }
    return 'http://localhost:8000';
  }

  // Single-container deploy: nginx serves API on the same origin
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin.replace(/\/$/, '');
  }

  if (isUsableApiUrl(runtime)) {
    return String(runtime).replace(/\/$/, '');
  }
  if (isUsableApiUrl(baked)) {
    return String(baked).replace(/\/$/, '');
  }

  return 'http://localhost:8000';
};

const config = {
  // API URL - supports both build-time and runtime configuration (Ringo-compatible alias)
  get API_URL() {
    return getApiUrl();
  },

  // API Base URL - alias for API_URL
  get API_BASE_URL() {
    return getApiUrl();
  },

  // Backend URL (alias for API_BASE_URL)
  get BACKEND_URL() {
    return reactAppEnv('REACT_APP_BACKEND_URL') || getApiUrl();
  },

  // Environment filter for garage tools (dev, test, prod/prd)
  get AGENT_ENVIRONMENT() {
    return reactAppEnv('REACT_APP_ENVIRONMENT') || null;
  },

  // Environment
  get ENVIRONMENT() {
    return reactAppEnv('REACT_APP_ENVIRONMENT') || 'development';
  },

  NODE_ENV: process.env.NODE_ENV || 'development',

  // Dev bypass for local browsers only (never Cloud Run hostnames)
  get DEV_MODE() {
    if (reactAppEnv('REACT_APP_DEV_MODE') !== 'true') return false;
    if (typeof window === 'undefined') return true;
    return isLocalBrowserHost(window.location.hostname);
  },

  get DEV_USER() {
    return reactAppEnv('REACT_APP_DEV_USER') || 'admin@ringcentral.com';
  },

  // Feature Flags
  get ENABLE_ANALYTICS() {
    return reactAppEnv('REACT_APP_ENABLE_ANALYTICS') === 'true';
  },

  /**
   * Escalate to IT BizOps button. Keep in sync with backend DOT_ESCALATION_ENABLED.
   * Default true when unset (local/dev). Set REACT_APP_DOT_ESCALATION_ENABLED=false to hide.
   */
  get DOT_ESCALATION_ENABLED() {
    return reactAppEnv('REACT_APP_DOT_ESCALATION_ENABLED') !== 'false';
  },

  /**
   * App access request flow (Discover Request access + Access requests nav).
   * Keep in sync with backend ACCESS_REQUESTS_ENABLED.
   * Default true when unset (local/dev). Set REACT_APP_ACCESS_REQUESTS_ENABLED=false to hide.
   */
  get ACCESS_REQUESTS_ENABLED() {
    return reactAppEnv('REACT_APP_ACCESS_REQUESTS_ENABLED') !== 'false';
  },

  /** Optional wiki links shown in the matching Access requests tab. */
  get ACCESS_REQUESTS_USER_WIKI_URL() {
    return reactAppEnv('REACT_APP_ACCESS_REQUESTS_USER_WIKI_URL') || '';
  },

  get ACCESS_REQUESTS_ADMIN_WIKI_URL() {
    return reactAppEnv('REACT_APP_ACCESS_REQUESTS_ADMIN_WIKI_URL') || '';
  },

  /**
   * Publish Gem from request button. Keep in sync with backend PUBLISH_GEM_ENABLED.
   * Default true when unset (local/dev). Set REACT_APP_PUBLISH_GEM_ENABLED=false to hide.
   */
  get PUBLISH_GEM_ENABLED() {
    return reactAppEnv('REACT_APP_PUBLISH_GEM_ENABLED') !== 'false';
  },

  /** Dynamic RingCentral bot onboarding and user-specific conversation launch. */
  get BOT_INTEGRATIONS_ENABLED() {
    return reactAppEnv('REACT_APP_BOT_INTEGRATIONS_ENABLED') === 'true';
  },

  /**
   * The unified "Scans" page (Security Scan + Code Review tabs — see
   * config/scanTypes.js). Keep in sync with backend SEC_SCAN_ENABLED.
   * Default true when unset. Set REACT_APP_SEC_SCAN_ENABLED=false to hide.
   */
  get SEC_SCAN_ENABLED() {
    return reactAppEnv('REACT_APP_SEC_SCAN_ENABLED') !== 'false';
  },

  /**
   * Immediate git access checks via the worker HTTP API.
   * Keep in sync with backend SEC_SCAN_WORKER_ACCESS_URL (set when that URL is
   * configured). When false, Verify access is hidden — access is checked on the
   * next worker job run. Runtime settings.accessImmediate overrides this after load.
   */
  get SEC_SCAN_ACCESS_IMMEDIATE() {
    return reactAppEnv('REACT_APP_SEC_SCAN_ACCESS_IMMEDIATE') === 'true';
  },

  /**
   * While any repo is pending/checking access, re-trigger an access check every
   * N minutes (calls Garage, which hits the worker when ACCESS_IMMEDIATE is on).
   * Default 5. Set to 0 to disable.
   */
  get SEC_SCAN_ACCESS_REFRESH_MINUTES() {
    const raw = reactAppEnv('REACT_APP_SEC_SCAN_ACCESS_REFRESH_MINUTES');
    if (raw === undefined || raw === null || raw === '') return 5;
    const n = Number.parseInt(String(raw), 10);
    if (!Number.isFinite(n) || n <= 0) return 0;
    return n;
  },

  /**
   * AES-GCM transport key for encrypting PATs before POST.
   * Must match backend SEC_SCAN_TOKEN_TRANSPORT_KEY (url-safe base64, 32 bytes).
   */
  get SEC_SCAN_TOKEN_TRANSPORT_KEY() {
    return (reactAppEnv('REACT_APP_SEC_SCAN_TOKEN_TRANSPORT_KEY') || '').trim();
  },

  /** Department-to-persona governance UI. Hidden by default. */
  get DEPARTMENTS_TAB_ENABLED() {
    return reactAppEnv('REACT_APP_DEPARTMENTS_TAB_ENABLED') === 'true';
  },

  /**
   * Comma-separated emails hidden from Tools Dashboard leaderboards (UI only).
   * Example: "dev1@ringcentral.com,dev2@ringcentral.com"
   */
  get DASHBOARD_EXCLUDED_USER_EMAILS() {
    return reactAppEnv('REACT_APP_DASHBOARD_EXCLUDED_USER_EMAILS') || '';
  },
};

export default config;
